import type { PantryItemDto } from '@fewerbags/schema';
import { STORAGE_LOCATIONS } from '@fewerbags/schema';

/** Pure display helpers for pantry items — no I/O, easy to unit test. */

export type StorageLocation = PantryItemDto['location'];

export const LOCATION_LABEL: Record<StorageLocation, string> = {
  pantry: 'Pantry',
  fridge: 'Fridge',
  freezer: 'Freezer',
};

export const LOCATION_ORDER: readonly StorageLocation[] = STORAGE_LOCATIONS;

export type ExpiryTone = 'expired' | 'soon' | 'ok' | 'none';

/** Urgency bucket for an item's expiry: already expired, within 3 days, further out, or unknown. */
export function expiryTone(daysUntilExpiry: number | null): ExpiryTone {
  if (daysUntilExpiry === null) return 'none';
  if (daysUntilExpiry < 0) return 'expired';
  if (daysUntilExpiry <= 3) return 'soon';
  return 'ok';
}

/** Short human label for an expiry badge: "Expired 2 days ago", "Expires today", "Expires in 5 days". */
export function expiryLabel(daysUntilExpiry: number | null): string {
  if (daysUntilExpiry === null) return 'No expiry date';
  if (daysUntilExpiry === 0) return 'Expires today';
  if (daysUntilExpiry === 1) return 'Expires tomorrow';
  if (daysUntilExpiry === -1) return 'Expired yesterday';
  if (daysUntilExpiry < 0) return `Expired ${Math.abs(daysUntilExpiry)} days ago`;
  return `Expires in ${daysUntilExpiry} days`;
}

/** Explains how `expiresOn` was determined, shown right after adding an item. */
export function expiresOnSourceLabel(source: PantryItemDto['expiresOnSource']): string {
  switch (source) {
    case 'ingredient':
    case 'category':
      return 'Estimated from shelf life';
    case 'manual':
      return 'You set the date';
    case 'unknown':
    default:
      return 'No expiry date set';
  }
}

/** `<input type="date">` value (YYYY-MM-DD, local) → the full ISO datetime the API expects, or undefined. */
export function dateInputToIsoDatetime(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const d = new Date(`${trimmed}T00:00:00`);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

/** An ISO datetime (or date) string → a `<input type="date">` value, or "" when absent. */
export function isoToDateInputValue(value: string | null | undefined): string {
  if (!value) return '';
  return value.slice(0, 10);
}

/** Group items by location in display order, dropping empty groups (mirrors the API's own grouping). */
export function groupByLocation<T extends { location: StorageLocation }>(
  items: readonly T[],
): Array<{ location: StorageLocation; items: T[] }> {
  return LOCATION_ORDER.map((location) => ({
    location,
    items: items.filter((i) => i.location === location),
  })).filter((g) => g.items.length > 0);
}
